import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactCropperImage from 'react-easy-crop';
import { ButtonPrimary, ButtonSecondary } from '@components/button';
import { SwitchToggle } from '@components/switch-toggle';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';

import { getCroppedImg } from './utils/get-cropped-img';
import { CropperAspectOption, CropperProps } from './cropper.type';

interface Area {
  width: number;
  height: number;
  x: number;
  y: number;
}

const getInitialAspectRatio = (
  fixed: number | undefined,
  options: readonly CropperAspectOption[] | undefined
): number => {
  if (fixed !== undefined) return fixed;
  if (options && options.length > 0) {
    const defaultOption = options.find((opt) => opt.isDefault) ?? options[0];
    return defaultOption.value;
  }
  return 1;
};

const getNextAspectRatio = (current: number, options: readonly CropperAspectOption[]): number => {
  const currentIndex = options.findIndex((opt) => opt.value === current);
  const nextIndex = (currentIndex + 1) % options.length;
  return options[nextIndex].value;
};

export const Cropper = (props: CropperProps) => {
  const { open, image, onSave, onCancel, onError, translations, imageFormat } = props;
  const aspectRatios = 'aspectRatios' in props ? props.aspectRatios : undefined;
  const fixedAspectRatio = 'aspectRatio' in props ? props.aspectRatio : undefined;
  const isToggleMode = !!aspectRatios;

  const initialRatio = useMemo(
    () => getInitialAspectRatio(fixedAspectRatio, aspectRatios),
    [fixedAspectRatio, aspectRatios]
  );

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [activeAspectRatio, setActiveAspectRatio] = useState(initialRatio);
  const [isSaving, setIsSaving] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setActiveAspectRatio(initialRatio);
      setIsSaving(false);
      cancelledRef.current = false;
    }
  }, [open, initialRatio]);

  const onCropComplete = useCallback((_: unknown, area: Area) => {
    setCroppedAreaPixels(area);
  }, []);

  const handleCancel = () => {
    cancelledRef.current = true;
    onCancel();
  };

  const handleSave = async () => {
    if (isSaving || !croppedAreaPixels) return;
    setIsSaving(true);
    try {
      const outputType = imageFormat ?? image.file.type;
      const result = await getCroppedImg(image.src, outputType, croppedAreaPixels, rotation);
      if (cancelledRef.current) return;
      if (result) onSave(result.file, { aspectRatio: activeAspectRatio });
    } catch (error: unknown) {
      if (cancelledRef.current) return;
      const err = error instanceof Error ? error : new Error(String(error));
      if (onError) onError(err);
      else console.error('Failed to crop image', err);
    } finally {
      if (!cancelledRef.current) setIsSaving(false);
    }
  };

  const handleZoom = (e: ChangeEvent<HTMLInputElement>) => setZoom(Number.parseFloat(e.target.value));
  const handleRotate = (e: ChangeEvent<HTMLInputElement>) => setRotation(Number.parseFloat(e.target.value));

  const handleAspectToggle = () => {
    if (!aspectRatios) return;
    const nextRatio = getNextAspectRatio(activeAspectRatio, aspectRatios);
    setActiveAspectRatio(nextRatio);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  };

  const isDefaultRatio =
    isToggleMode && aspectRatios
      ? activeAspectRatio === (aspectRatios.find((opt) => opt.isDefault) ?? aspectRatios[0]).value
      : false;

  return (
    <Transition show={open} as={Fragment} appear={true}>
      <Dialog className='relative z-50' open={open} onClose={isSaving ? () => {} : handleCancel}>
        <TransitionChild
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 backdrop-overlay transition-opacity' aria-hidden='true' />
        </TransitionChild>

        <div className='fixed inset-0 flex items-center justify-center p-4'>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'>
            <DialogPanel className='bg-surface-primary rounded-lg w-full max-w-2xl shadow-xl transform transition-all'>
              <div className='px-4 pt-5 pb-4 sm:p-6'>
                <div className='relative w-full h-96'>
                  <ReactCropperImage
                    image={image.src}
                    crop={crop}
                    rotation={rotation}
                    zoom={zoom}
                    aspect={activeAspectRatio}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>

                {isToggleMode && translations.aspectRatioToggleLabel && (
                  <div className='mt-4 sm:mt-6 flex items-center gap-3'>
                    <SwitchToggle
                      active={isDefaultRatio}
                      loading={false}
                      onToggleSwitch={handleAspectToggle}
                      disabled={false}
                    />
                    <div className='flex flex-col'>
                      <span className='text-secondary text-subtitle-s'>{translations.aspectRatioToggleLabel}</span>
                      {translations.aspectRatioToggleSublabel && (
                        <span className='text-tertiary text-body-xs'>{translations.aspectRatioToggleSublabel}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className='mt-4 sm:mt-6 grid grid-rows-2 md:grid-rows-1 md:grid-cols-2 gap-y-4 gap-x-8'>
                  <div className='flex flex-row space-x-4'>
                    <label className='text-primary' htmlFor='cropper-zoom'>
                      {translations.zoom}
                    </label>
                    <input
                      id='cropper-zoom'
                      name='zoom'
                      className='text-primary'
                      value={zoom}
                      type='range'
                      onChange={handleZoom}
                      min='1'
                      max='3'
                      step='0.1'
                    />
                  </div>

                  <div className='flex flex-row space-x-4'>
                    <label className='text-primary' htmlFor='cropper-rotate'>
                      {translations.rotate}
                    </label>
                    <input
                      id='cropper-rotate'
                      name='rotate'
                      value={rotation}
                      type='range'
                      onChange={handleRotate}
                      min={0}
                      max={360}
                      step={1}
                    />
                  </div>
                </div>

                <div className='flex justify-end mt-4 sm:mt-6 gap-4'>
                  <ButtonSecondary
                    type='button'
                    data-testid='cropper__cancel'
                    onClick={handleCancel}
                    disabled={isSaving}>
                    {translations.cancel}
                  </ButtonSecondary>

                  <ButtonPrimary type='button' data-testid='cropper__save' onClick={handleSave} disabled={isSaving}>
                    {translations.save}
                  </ButtonPrimary>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
